import { atomWithGlobalState, atomWithGlobalStore } from '@highlight/ipc/jotai'
import { atom, useAtomValue, useSetAtom } from 'jotai'
import { ScreenshotSources, SelectableContext } from '../types'
import { useCallback, useMemo } from 'react'
import { useLastFocusedWindow } from './runtime'
import { useActiveChatId } from './chats'
import { useActiveAudioNoteId } from './audio-notes'
import { useAttachmentStore, type ClearAttachmentsOptions } from './attachments'
import { useDiscardLocalPaths } from './local-paths'
import { ExplicitWindowContextScope } from '@highlight/common'
import { webviewContextAtom, optimisticWebviewContextAtom } from './webview'
import { useDefaultWindowScope } from '../hooks/useDefaultWindowScope'

/**
 * Generic atoms file, used more for "state" vs "store".
 */

/*
 * Voice mode atoms
 */
const voiceModeActiveAtom = atomWithGlobalState<boolean>('ptt.pressed', false)
export const useIsVoiceModeActive = () => useAtomValue(voiceModeActiveAtom)

const voiceModeHotkeyAtom = atomWithGlobalStore<string>(
  'settings',
  'hotkeys.voiceMode',
  process.platform === 'darwin' ? 'Cmd' : 'Ctrl',
)
export const useVoiceModeHotkey = () => useAtomValue(voiceModeHotkeyAtom)

const isDictatingAtom = atom(false)
export const useIsDictating = () => useAtomValue(isDictatingAtom)
export const useSetIsDictating = () => useSetAtom(isDictatingAtom)

/**
 * Current context that Highlight is auto-analyzing.
 */
const selectableContextAtom = atomWithGlobalState<SelectableContext | undefined>('selectableContext', undefined)
export const useSelectableContext = () => useAtomValue(selectableContextAtom)

const isRequestingAtom = atomWithGlobalState<boolean>('assistant.requestingContext', false)
export const useIsRequestingContext = () => useAtomValue(isRequestingAtom)

/**
 * This is used to cache the sources available for context (windows and displays).
 */
export const contextSourcesAtom = atom<ScreenshotSources>({ windows: [], displays: [] })

/**
 * Current auto-focus window scope.
 * Defaults to 'visible' but can be controlled via LaunchDarkly flag 'default-to-full-window-context'
 */
const currentWindowScope = atom<ExplicitWindowContextScope>('visible')
export const useCurrentWindowScope = () => useAtomValue(currentWindowScope)
export const useSetCurrentWindowScope = () => useSetAtom(currentWindowScope)

const currentWindowDisabled = atom(true)
export const useIsCurrentWindowDisabled = () => useAtomValue(currentWindowDisabled)
export const useSetCurrentWindowDisabled = () => useSetAtom(currentWindowDisabled)

/**
 * Convert the current auto-focus context to a window selection.
 */
export const useCurrentWindowToSelection = () => {
  const context = useSelectableContext()
  const scope = useCurrentWindowScope()
  const window = useLastFocusedWindow()
  const isLoading = useIsRequestingContext()

  return useMemo(() => {
    if (!window?.windowTitle?.length && !window?.appName?.length) {
      return undefined
    }

    return {
      ...window,
      context,
      isLoading,
      scope,
    } as WindowSelectionType
  }, [isLoading, context, scope, window])
}

// Atom to track window-contents permission
const windowContentsPermissionAtom = atomWithGlobalStore<'detect' | 'notify' | 'locked'>(
  'settings',
  'attachmentAndDetectionPermissions.window-contents',
  'detect',
)

const incognitoModeAtom = atomWithGlobalStore<boolean>('general', 'incognitoMode', false)

export const useIsCurrentWindowAttached = () => {
  const currentWindow = useCurrentWindowToSelection()
  const selectedWindows = useSelectedWindows()
  const unselectedWindows = useUnselectedWindows()
  const activeChatId = useActiveChatId()
  const activeAudioNoteId = useActiveAudioNoteId()
  const isCurrentWindowDisabled = useIsCurrentWindowDisabled()
  const windowContentsPermission = useAtomValue(windowContentsPermissionAtom)
  const isIncognito = useAtomValue(incognitoModeAtom)

  return useMemo(() => {
    if (isIncognito) {
      return false
    }
    if (isCurrentWindowDisabled) {
      return false
    }
    if (!currentWindow) {
      return false
    }
    if (activeChatId) {
      return false
    }
    if (activeAudioNoteId) {
      return false
    }
    // Check if window-contents permission allows detection
    if (windowContentsPermission === 'locked') {
      return false
    }
    return selectedWindows.length === 0 && unselectedWindows.length === 0 && window.__RENDERER_NAME !== 'app'
  }, [
    currentWindow,
    activeChatId,
    activeAudioNoteId,
    selectedWindows,
    unselectedWindows,
    isIncognito,
    isCurrentWindowDisabled,
    windowContentsPermission,
  ])
}

export interface ResetChatContextOptions extends ClearAttachmentsOptions {
  /** The attachments went out with a message, so the chat keeps its access to their local paths. */
  keepLocalPathGrants?: boolean
}

export const useResetChatContext = () => {
  const { clearAttachments } = useAttachmentStore()
  const discardLocalPaths = useDiscardLocalPaths()
  const { clearWindowSelections } = useWindowSelection()
  const setCurrentWindowScope = useSetCurrentWindowScope()
  const defaultWindowScope = useDefaultWindowScope()
  const setCurrentWindowDisabled = useSetCurrentWindowDisabled()
  const setWebviewContext = useSetAtom(webviewContextAtom)
  const setOptimisticWebviewContext = useSetAtom(optimisticWebviewContextAtom)

  return useCallback(
    (options?: ResetChatContextOptions) => {
      const dropped = clearAttachments(options)
      if (!options?.keepLocalPathGrants) {
        discardLocalPaths(dropped)
      }
      clearWindowSelections()
      setCurrentWindowScope(defaultWindowScope)
      setCurrentWindowDisabled(true)
      setWebviewContext(null)
      setOptimisticWebviewContext(null)
    },
    [
      clearAttachments,
      discardLocalPaths,
      clearWindowSelections,
      setCurrentWindowScope,
      defaultWindowScope,
      setCurrentWindowDisabled,
      setWebviewContext,
      setOptimisticWebviewContext,
    ],
  )
}

/**
 * Window Selection is used when the user manually selects/unselects window context.
 * It manages the entire state per-window. It is used directly to attach on message submission.
 */
export type WindowSelectionType = {
  pid: number
  appName: string
  windowTitle: string
  appIcon?: string | null
  url?: string | null
  scope?: ExplicitWindowContextScope
  context?: SelectableContext
  isLoading?: boolean
  tabId?: number
}

export const selectedWindowsAtom = atom<WindowSelectionType[]>([])
export const unselectedWindowsAtom = atom<WindowSelectionType[]>([])

export const useSelectedWindows = () => useAtomValue(selectedWindowsAtom)
export const useUnselectedWindows = () => useAtomValue(unselectedWindowsAtom)
export const useSetSelectedWindows = () => useSetAtom(selectedWindowsAtom)
export const useSetUnselectedWindows = () => useSetAtom(unselectedWindowsAtom)

const containsWindow = (windows: WindowSelectionType[], window: WindowSelectionType) => {
  return windows.some(
    (w) => w.pid === window.pid && w.windowTitle === window.windowTitle && w.appName === window.appName,
  )
}

export const useWindowSelection = () => {
  const selectedWindows = useSelectedWindows()
  const setSelectedWindows = useSetSelectedWindows()
  const unselectedWindows = useUnselectedWindows()
  const setUnselectedWindows = useSetUnselectedWindows()

  const isWindowSelected = useCallback(
    (window: WindowSelectionType) => {
      return containsWindow(selectedWindows, window)
    },
    [selectedWindows],
  )

  const isWindowUnselected = useCallback(
    (window: WindowSelectionType) => {
      return containsWindow(unselectedWindows, window)
    },
    [unselectedWindows],
  )

  const addSelectedWindow = useCallback(
    (window: WindowSelectionType) => {
      setSelectedWindows((prev) => {
        if (containsWindow(prev, window)) {
          return prev
        }
        return [...prev, window]
      })
      setUnselectedWindows((prev) => {
        return prev.filter(
          (w) => w.pid !== window.pid && w.windowTitle !== window.windowTitle && w.appName !== window.appName,
        )
      })
    },
    [setSelectedWindows, setUnselectedWindows],
  )

  const resetWindow = useCallback(
    (window: WindowSelectionType) => {
      setSelectedWindows((prev) => {
        return prev.filter(
          (w) => w.pid !== window.pid && w.windowTitle !== window.windowTitle && w.appName !== window.appName,
        )
      })
      setUnselectedWindows((prev) => {
        return prev.filter(
          (w) => w.pid !== window.pid && w.windowTitle !== window.windowTitle && w.appName !== window.appName,
        )
      })
    },
    [setUnselectedWindows],
  )

  const updateSelectedWindow = useCallback(
    (window: WindowSelectionType, update: Partial<WindowSelectionType>) => {
      setSelectedWindows((prev) => {
        const index = prev.findIndex(
          (w) => w.pid === window.pid && w.windowTitle === window.windowTitle && w.appName === window.appName,
        )
        if (index !== -1) {
          const modified = prev.slice(0)
          modified[index] = {
            ...modified[index],
            ...update,
          }
          return modified
        }
        console.warn('skipped update, window not selected:', window, prev)
        return prev
      })
    },
    [setSelectedWindows],
  )

  const removeSelectedWindow = useCallback(
    (window: WindowSelectionType) => {
      setSelectedWindows((prev) => {
        return prev.filter(
          (w) => w.pid !== window.pid || w.windowTitle !== window.windowTitle || w.appName !== window.appName,
        )
      })
      setUnselectedWindows((prev) => {
        if (containsWindow(prev, window)) {
          return prev
        }
        return [...prev, window]
      })
    },
    [setSelectedWindows, setUnselectedWindows],
  )

  const clearWindowSelections = useCallback(() => {
    setSelectedWindows([])
    setUnselectedWindows([])
  }, [setSelectedWindows, setUnselectedWindows])

  return useMemo(() => {
    return {
      isWindowSelected,
      isWindowUnselected,
      addSelectedWindow,
      removeSelectedWindow,
      clearWindowSelections,
      updateSelectedWindow,
      resetWindow,
    }
  }, [isWindowSelected, isWindowUnselected, addSelectedWindow, removeSelectedWindow, updateSelectedWindow, resetWindow])
}
